import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';

import { RouteDetailViewModel } from '../viewModels/route-detail-viewmodel';
import { MapsService } from '../../maps/services/maps.service';
import { JobDomainService } from '../../domain/services/job/job.domain.service';
import { Job } from '../../domain/models/job';
import { Size } from '../../domain/models/size';

import { LinegraphDomainService } from '../../domain/services/linegraph/linegraph.domain.service';
import { SpectrogramDomainService } from '../../domain/services/spectrogram/spectrogram.domain.service';
import { HistogramDomainService } from '../../domain/services/histogram/histogram.domain.service';
import { LineGraph } from '../../domain/models/linegraphData';
import { PerspectiveGraphValue } from '../../domain/models/perspectiveGraphValue';
import { SpectrogramData, SpectrogramValue, SpectrogramSectionValue, Path } from '../../domain/models/spectrogramData';
import { HistogramData } from '../../domain/models/histogramData';

import * as jspdf from 'jspdf';
import * as _ from 'lodash';
import * as moment from 'moment';
import { AuthService } from 'app/modules/auth/services/auth.service';
import { QuotaDomainService } from '../../domain/services/quota/quota.domain.service';
import { Project } from '../../domain/models/project';
import { Polyline } from '../../domain/models/polyline';
import { Scales } from 'app/modules/domain/models/scales';

@Injectable()
export class RouteDetailService {

  private cachedWaypoints: any;

  constructor(private mapsService: MapsService,
    private jobDomainService: JobDomainService,
    private linegraphDomainService: LinegraphDomainService,
    private spectrogramDomainService: SpectrogramDomainService,
    private histogramDomainService: HistogramDomainService,
    private authService: AuthService,
    private quotaDomainService: QuotaDomainService
  ) {
    this.cachedWaypoints = [];
  }

  getRouteDetails(routeId: string): Observable<void | RouteDetailViewModel> {
    return this.jobDomainService.getJob(routeId)
      .map((job: Job) => {
        return new RouteDetailViewModel(job);
      }, (error: any) => {
        console.log(error);
      });
  }

  getRoute(route: any): Promise<any> {
    return this.mapsService.getRoute(route.startCoords, route.endCoords, route.wayPoints);
  }

  getProjectById(projectId: string): Observable<{} | Project> {
    return this.jobDomainService.getProjectById(projectId);
  }

  getPreviousRoute(): string {
    return this.authService.getPreviousRoute();
  }

  drawPersistedRoute(polyline: Polyline) {
    this.mapsService.drawPersistedRoute(polyline);
  }

  drawRoute(polyline: Polyline) {
    this.mapsService.drawPersistedRoute(polyline);
  }

  drawOtherRoutes(selectedPolylines: Polyline[], otherPolylines: Polyline[]): void {
    this.mapsService.drawSelectedRoutes(selectedPolylines);
    this.mapsService.drawPersistedRoutes(otherPolylines);
  }

  clearOtherRoutes(): void {
    this.mapsService.clearMapOverlays(true);
  }

  clearCachedPolyLines(): void {
    this.mapsService.clearCachedPolyLines();
  }
  clearCachedBranchingPolyLines(): void {
    this.mapsService.removeBranchingLines();
  }
  getDataForSettings(date: Date, jobId: string): Observable<{} | LineGraph> {
    return this.linegraphDomainService.getLineGraphData(date, jobId);
  }

  getMultiDaylineGraph(jobId: string, dates: Date[]): Observable<{} | LineGraph[]> {
    return this.linegraphDomainService.getMultiDaylineGraph(jobId, dates);
  }

  getLineGraphCsv(jobId: string, dates: Date[], filename: string): void {
    return this.linegraphDomainService.getLineGraphCsv(jobId, dates, filename);
  }
  getSpectrogramCsv(jobId: string, dates: Date[], filename: string): void {
    return this.spectrogramDomainService.getSpectrogramCsv(jobId, dates, filename);
  }

  getPerspective(jobId: string, dayOfWeek: number): Observable<{} | PerspectiveGraphValue> {
    return this.linegraphDomainService.getPerspectiveData(jobId, dayOfWeek);
  }

  reverseGeocode(latlng: any) {
    return this.mapsService.reverseGeoCode(latlng);
  }

  getSpectogramData(jobId: string, date: Date): Observable<{} | SpectrogramData> {
    return this.spectrogramDomainService.getSpectrogramData(jobId, date);
  }

  createPdf(element: any, routeName: string, startAddress: string, endAddress: string, subText: string, date: Date): void {
    let doc = new jspdf('landscape');
    let frameWidth = 296;
    doc.setFontSize(16);

    doc.text(routeName + ' ' + moment(date).format('dddd DD/MM/YYYY'), 10, 10);
    doc.setFontSize(12);
    doc.text(startAddress + ' - ' + endAddress, 10, 20);
    doc.addImage(element, 'PNG', 3, 40, 290, 145);

    if (subText) {
      let textWidth = doc.getStringUnitWidth(subText) * 12 / doc.internal.scaleFactor;
      let xPos = (frameWidth - textWidth) / 2;
      doc.text(subText, xPos, 180);
    }

    let filename = moment(date).format('YYYYMMDD') + '_' + routeName;
    doc.save(filename + '.pdf');
  }

  createSpectroPdf(element1: any, element2: any, element2Width: number, element2Height: number, routeName: string, startAddress: string, endAddress: string, subText: string, date: Date): void {
    let doc = new jspdf('landscape');

    let frameWidth = 296;
    doc.setFontSize(16);

    doc.text(routeName + ' ' + moment(date).format('dddd DD/MM/YYYY'), 10, 10);
    doc.setFontSize(12);
    doc.text(startAddress + ' - ' + endAddress, 10, 20);

    let xPos = 3;
    let yPos = 30;

    let el2RatioX = 290 / element2Width;
    let el2RatioY = 145 / element2Height;
    let el2Ratio = el2RatioX > el2RatioY ? el2RatioY : el2RatioX;

    let el2Width = element2Width * el2Ratio;
    let el2Height = element2Height * el2Ratio;

    let el1MarginLeft = (70 * el2Ratio) + xPos;
    let el1MarginRight = 90 * el2Ratio;
    let el1MarginBottom = 115 * el2Ratio;

    let el2RelativeMarginTop = 30 * el2Ratio;
    let el2MarginTop = (20 * el2Ratio) + yPos;

    let e1Width = el2Width - el1MarginRight;
    let e1Height = el2Height - el1MarginBottom - el2RelativeMarginTop;

    doc.addImage(element1, 'PNG', el1MarginLeft, el2MarginTop, e1Width, e1Height);
    doc.addImage(element2, 'PNG', xPos, yPos, el2Width, el2Height);

    if (subText) {
      let textWidth = doc.getStringUnitWidth(subText) * 12 / doc.internal.scaleFactor;
      let textXPos = (frameWidth - textWidth) / 2;
      doc.text(subText, textXPos, 180);
    }

    let filename = moment(date).format('YYYYMMDD') + '_' + routeName;
    doc.save(filename + '.pdf');
  }

  drawMarker(markerUrl: string, size: Size, location: any) {
    this.mapsService.clearTempMarkers();
    this.mapsService.setTempMarker(markerUrl, size, location);
  }

  getHistogram(jobId: string, date: Date, bin: number): Observable<{} | HistogramData> {
    return this.histogramDomainService.getHistogram(jobId, date, bin);
  }

  checkStreetViewAvailability(location: any): Observable<boolean> {
    return this.mapsService.checkStreetViewAvailability(location);
  }

  enableSplitView(location: any): void {
    this.mapsService.splitStreetView(location);
  }

  closeSplitView(): void {
    this.mapsService.closeSplitStreetView();
  }

  clearMarkers(): void {
    this.mapsService.clearTempMarkers();
  }

  clearMap(): void {
    this.mapsService.clearMapOverlays(true);
  }

  resetMap(): void {
    this.mapsService.resetMap();
  }

  clearCurrentRouteFromMap(): void {
    this.mapsService.clearRoute();
    this.mapsService.clearSpectroPolylines();
  }

  clearCurrentRouteFromMapDelayed(): void {
    this.mapsService.clearRouteDelayed();
    this.mapsService.clearSpectroPolylines();
  }

  clearSpectroPolylinesFromMap(): void {
    this.mapsService.clearSpectroPolylines();
  }

  clearOtherRoutesFromMap(): void {
    this.mapsService.clearMapOverlays(false);
  }

  drawMapSpectrogram(path: Path[], data: SpectrogramValue, maxValue: number, colorScale: any[], type: string): void {
    this.mapsService.drawSpectrogramOnMap(path, data, maxValue, colorScale, type);
  }

  cacheWaypoints(wayPoints: any, id: string): void {
    this.cachedWaypoints.push({ wayPoints: wayPoints, id: id });
  }
  deleteCachedWaypoints(): void {
    this.cachedWaypoints = [];
  }
  getCachedWaypoints(id: string): any {
    let point = _.find(this.cachedWaypoints, (wp: any) => {
      return wp.id === id;
    });
    if (point !== undefined) {
      return point.wayPoints;
    } else {
      return undefined;
    }
  }

  getAllUnfilterdJobs(): Observable<{} | Job[]> {
    return this.jobDomainService.getAllUnfilteredJobs();
  }

  getScalesForJob(jobId: string): Scales {
    let scalesList: Scales[] = JSON.parse(localStorage.getItem('scales'));
    if (!scalesList) { return undefined; }
    return scalesList.find(s => s.jobId === jobId);
  }

  saveScalesForJob(newScales: Scales): void {
    let scalesList: Scales[] = JSON.parse(localStorage.getItem('scales'));
    let existingScales: Scales;
    if (!scalesList) {
      scalesList = [];
    } else {
      existingScales = scalesList.find(s => s.jobId === newScales.jobId);
      if (existingScales) {
        const index = scalesList.indexOf(existingScales);
        if (index > -1) {
          scalesList.splice(index, 1);
        }
      }
    }
    scalesList.push(newScales);
    localStorage.setItem('scales', JSON.stringify(scalesList));
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/route-detail/services/route-detail.service.ts